import React from "react";
import { Grid, MenuItem, Paper, Select, Typography } from "@material-ui/core";
import { isEmpty, sum } from "lodash";
import moment from "moment";
import PageDomain from "../domain";
import PageConfig from "../config";

const TotalSalesLineChart = ({ orderedSalesMap }) => {
  const { SALES_TARGET_STARTED_DATE, LEGEND_DATE_FORMAT } = PageConfig;
  const canvasRef = React.useRef(null);
  const chartRef = React.useRef(null);

  const [chartDateset, setChartDataset] = React.useState({});
  const [chartType, setChartType] = React.useState("line");
  const [salesMonth, setSalesMonth] = React.useState("all");

  React.useEffect(() => {
    if (isEmpty(orderedSalesMap)) return;
    const chartDateset = PageDomain.generateTotalSalesChartDataset({
      orderedSalesMap,
      chartType,
    });
    setChartDataset(chartDateset);
  }, [chartType, orderedSalesMap]);

  React.useEffect(() => {
    if (canvasRef.current == null || !window.Chart || isEmpty(chartDateset)) {
      return;
    }

    if (chartRef.current === null) {
      const ctx = canvasRef.current.getContext("2d");
      chartRef.current = new window.Chart(ctx, {
        type: chartType,
        data: chartDateset,
        options: {
          responsive: true,
        },
      });
    } else {
      chartRef.current.config.type = chartType;
      chartRef.current.data.datasets = chartDateset.datasets;
      chartRef.current.data.labels = chartDateset.labels;
      switch (chartType) {
        case "pie":
          chartRef.current.options.scales.x.display = false;
          chartRef.current.options.scales.y.display = false;
          chartRef.current.options.plugins.tooltip.callbacks.label = (
            context
          ) => {
            const total = sum(context.dataset.data);
            const currentValue = context.dataset.data[context.dataIndex];
            const percentage = ((currentValue / total) * 100).toFixed(2);
            return `${currentValue} (${percentage}%)`;
          };
          break;
        default:
          chartRef.current.options.scales.x.display = true;
          chartRef.current.options.scales.y.display = true;
          break;
      }
      chartRef.current.update();
    }
  }, [chartDateset, chartType]);

  const fromDate = moment(SALES_TARGET_STARTED_DATE).format(LEGEND_DATE_FORMAT);
  const toDate = moment().format(LEGEND_DATE_FORMAT);

  return (
    <Paper elevation={2} className="chart-paper">
      <Grid container direction="column" className="chart-panel">
        <Grid container direction="row">
          <Grid item xs={12} md={4} container direction="column">
            <Typography variant="h6">Total Sales</Typography>
            <Typography variant="caption">
              (<b>From:</b> {fromDate} <b>To:</b> {toDate})
            </Typography>
          </Grid>
          <Grid item xs={12} md container direction="row">
            <Grid
              item
              xs={6}
              container
              direction="row"
              className="gap-x-8"
              alignItems="center"
            >
              <Typography variant="caption">Chart Type</Typography>
              <Select
                variant="standard"
                value={chartType}
                onChange={(event) => {
                  setChartType(event.target.value);
                }}
              >
                <MenuItem value="line">Line</MenuItem>
                <MenuItem value="pie">Pie</MenuItem>
              </Select>
            </Grid>
            <Grid
              item
              xs={6}
              container
              direction="row"
              className="gap-x-8"
              alignItems="center"
              justifyContent="flex-start"
              wrap="nowrap"
            >
              <Typography variant="caption">Sales Month</Typography>
              <Select
                variant="standard"
                value={salesMonth}
                onChange={(event) => {
                  setSalesMonth(event.target.value);
                }}
              >
                <MenuItem value="all">All Months</MenuItem>
                <MenuItem value="september">September</MenuItem>
                <MenuItem value="octomber">Octomber</MenuItem>
              </Select>
            </Grid>
          </Grid>
        </Grid>
        <canvas ref={canvasRef}></canvas>
      </Grid>
    </Paper>
  );
};

export default TotalSalesLineChart;
